import { useCallback, useRef, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Close, MoreHorizOutlined } from "@mui/icons-material";
import { BTX_ANALYST_NOTEPAD } from "@platform/feature-flags";
import { FeatureFlagBoundary } from "@platform/fe-utils";
import { Typography } from "@platform/design-system";

import AnalystNoteContent from "./AnalystNoteContent";
import CarrierNotesPanel from "./CarrierNotesPanel";
import CoverageNoteContent from "./CoverageNoteContent";
import RFPNoteContent from "./RfpNoteContent";

interface Props {
  data: {
    ecoRfpId: string;
    brokerExternalId: string;
    clientName: string;
  };
  onClose: VoidFunction;
}

const BRAND_PURPLE_COLOR = "brandPrimary.main";

const tabsSx = {
  px: 2,
  borderBottom: "1px solid",
  borderColor: "divider",
  "& .Mui-selected": {
    color: BRAND_PURPLE_COLOR,
  },
  "& .MuiTabs-indicator": {
    height: "1px",
    backgroundColor: BRAND_PURPLE_COLOR,
  },
} as const;

const DrawerHeader = ({
  onClose,
}: {
  onClose: VoidFunction;
}): React.JSX.Element => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    px={2}
    py={2.5}
  >
    <Typography variant="body-large" component="h2">
      Notes
    </Typography>
    <Box display="flex" alignItems="center" gap={1}>
      <IconButton aria-label="More options" disabled>
        <MoreHorizOutlined fontSize="small" />
      </IconButton>
      <IconButton aria-label="Close" onClick={onClose}>
        <Close fontSize="small" />
      </IconButton>
    </Box>
  </Box>
);

const ANALYST_TAB = "analyst-notepad";
const CARRIER_TAB = "carrier-notes";
type AnalystNotepadTab = typeof ANALYST_TAB | typeof CARRIER_TAB;

const NoteTabSelector = ({
  value,
  onChange,
}: {
  value: AnalystNotepadTab;
  onChange: (value: AnalystNotepadTab) => void;
}): React.JSX.Element => (
  <ToggleButtonGroup
    exclusive
    fullWidth
    value={value}
    onChange={(_event, next: AnalystNotepadTab | null) => {
      if (next) {
        onChange(next);
      }
    }}
    aria-label="Note tabs"
    sx={{
      mb: 1,
      px: 2,
      "& .MuiToggleButton-root": {
        textTransform: "none",
        color: BRAND_PURPLE_COLOR,
        borderColor: BRAND_PURPLE_COLOR,
        py: 0.75,
        "&.Mui-selected": {
          color: "common.white",
          backgroundColor: BRAND_PURPLE_COLOR,
          "&:hover": { backgroundColor: BRAND_PURPLE_COLOR },
        },
      },
    }}
  >
    <ToggleButton
      value={ANALYST_TAB}
      id="tab-analyst-notepad"
      aria-controls="tabpanel-analyst-notepad"
    >
      Analyst Notepad
    </ToggleButton>
    <ToggleButton
      value={CARRIER_TAB}
      id="tab-carrier-notes"
      aria-controls="tabpanel-carrier-notes"
    >
      Carrier Notes
    </ToggleButton>
  </ToggleButtonGroup>
);

const AnalystNotepadDrawer = ({ data, onClose }: Props): React.JSX.Element => {
  const { ecoRfpId } = data;
  const [selectedTab, setSelectedTab] =
    useState<AnalystNotepadTab>(ANALYST_TAB);

  // Both panels stay mounted so a tab switch never discards a draft; leaving a
  // tab flushes its save while still mounted.
  const analystFlushRef = useRef<() => void>(() => undefined);
  const carrierFlushRef = useRef<() => void>(() => undefined);
  const registerAnalystFlush = useCallback((fn: () => void) => {
    analystFlushRef.current = fn;
  }, []);
  const registerCarrierFlush = useCallback((fn: () => void) => {
    carrierFlushRef.current = fn;
  }, []);

  const handleTabChange = (next: AnalystNotepadTab): void => {
    if (next === selectedTab) return;
    const leavingFlush =
      selectedTab === ANALYST_TAB ? analystFlushRef : carrierFlushRef;
    leavingFlush.current();
    setSelectedTab(next);
  };

  const isAnalystTab = selectedTab === ANALYST_TAB;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DrawerHeader onClose={onClose} />
      <NoteTabSelector value={selectedTab} onChange={handleTabChange} />
      <Divider />
      <Box
        sx={{
          p: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: isAnalystTab ? "flex" : "none",
            flexDirection: "column",
          }}
        >
          <AnalystNoteContent
            ecoRfpId={ecoRfpId}
            registerFlush={registerAnalystFlush}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: isAnalystTab ? "none" : "flex",
            flexDirection: "column",
          }}
        >
          <CarrierNotesPanel
            ecoRfpId={ecoRfpId}
            onClose={onClose}
            registerFlush={registerCarrierFlush}
          />
        </Box>
      </Box>
    </Box>
  );
};

type LegacyNoteTab = "rfp" | "coverages";

const LegacyRFPNoteDrawer = ({ data, onClose }: Props): React.JSX.Element => {
  const { ecoRfpId } = data;
  const [selectedTab, setSelectedTab] = useState<LegacyNoteTab>("rfp");

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DrawerHeader onClose={onClose} />
      <Tabs
        sx={tabsSx}
        value={selectedTab}
        onChange={(_event, newValue) => setSelectedTab(newValue)}
        aria-label="RFP note tabs"
      >
        <Tab
          label="RFP"
          id="tab-rfp"
          value="rfp"
          aria-controls="tabpanel-rfp"
        />
        <Tab
          label="Coverages"
          id="tab-coverages"
          value="coverages"
          aria-controls="tabpanel-coverages"
        />
      </Tabs>
      <Box
        sx={{
          p: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {selectedTab === "rfp" && (
          <RFPNoteContent ecoRfpId={ecoRfpId} onClose={onClose} />
        )}
        {selectedTab === "coverages" && (
          <CoverageNoteContent ecoRfpId={ecoRfpId} />
        )}
      </Box>
    </Box>
  );
};

const RFPNoteDrawer = ({ data, onClose }: Props): React.JSX.Element => (
  <>
    <FeatureFlagBoundary
      flag={BTX_ANALYST_NOTEPAD}
      fallback={<LegacyRFPNoteDrawer data={data} onClose={onClose} />}
    >
      <AnalystNotepadDrawer data={data} onClose={onClose} />
    </FeatureFlagBoundary>
  </>
);

export default RFPNoteDrawer;
